// See App.tsx's own comment on this same import -- required for the
// classic JSX transform this app's tsconfig uses.
import * as React from 'react';
import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import type { ApplicationFormContentKey } from './content-client';
import type { FieldName, FormDraft } from './EiApplicationForm';

type AddressValue = {
  line1?: string;
  line2?: string;
  city?: string;
  province?: string;
  postalCode?: string;
};

export interface AddressFieldProps {
  draft: FormDraft;
  setDraft: Dispatch<SetStateAction<FormDraft>>;
  label: (key: ApplicationFormContentKey) => string;
  fieldError: (field: FieldName) => string | undefined;
  disabled?: boolean;
}

/**
 * The editable address surface on step 1's "About you" card (spec 014
 * consolidated it there from the old Personal information and Confirm
 * your details steps). Pre-filled from citizen-context; the form writes a
 * changed address back when the citizen continues. Kept as its own
 * component specifically so the imperative `value` sync and the
 * `scdsAddressChange` listener mount with the widget when step 1 renders
 * -- a form-root effect wouldn't re-fire when navigating between steps.
 *
 * scds-address-input-group's `value` is a non-primitive object; Stencil
 * never reflects those via an attribute (platform CLAUDE.md's
 * "scds-multi-column-list gotcha"), so it's set imperatively on the ref.
 * React 19 won't wire a JSX `onScdsAddressChange` prop to a real
 * addEventListener either, so the change event is caught via a delegated
 * listener on this component's own root ref -- same pattern as
 * msca-shell's ProfilePage.tsx.
 */
export function AddressField({ draft, setDraft, label, fieldError, disabled }: AddressFieldProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const addressRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = addressRef.current as (HTMLElement & { value?: AddressValue }) | null;
    if (!el) {
      return;
    }
    el.value = {
      line1: draft.addressLine1,
      line2: draft.addressLine2,
      city: draft.city,
      province: draft.province,
      postalCode: draft.postalCode,
    };
  }, [draft.addressLine1, draft.addressLine2, draft.city, draft.province, draft.postalCode]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    function handleAddress(event: Event): void {
      const detail = (event as CustomEvent<AddressValue>).detail;
      setDraft((prev) => ({
        ...prev,
        addressLine1: detail.line1 ?? '',
        addressLine2: detail.line2 ?? '',
        city: detail.city ?? '',
        province: detail.province ?? '',
        postalCode: detail.postalCode ?? '',
      }));
    }
    root.addEventListener('scdsAddressChange', handleAddress);
    return () => root.removeEventListener('scdsAddressChange', handleAddress);
  }, [setDraft]);

  return (
    <div ref={rootRef}>
      <scds-address-input-group
        ref={addressRef}
        label={label('employment-insurance.application.addressLabel')}
        line1-label={label('employment-insurance.application.addressLine1Label')}
        line2-label={label('employment-insurance.application.addressLine2Label')}
        city-label={label('employment-insurance.application.cityLabel')}
        province-label={label('employment-insurance.application.provinceLabel')}
        postal-code-label={label('employment-insurance.application.postalCodeLabel')}
        disabled={disabled}
      />
      {fieldError('addressLine1') || fieldError('city') || fieldError('province') || fieldError('postalCode') ? (
        <p role="alert" className="ei-form__error">
          {label('employment-insurance.application.requiredError')}
        </p>
      ) : null}
    </div>
  );
}
